import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePermissions, ViewOnlyBanner } from "@/hooks/use-permissions";
import type { Property } from "@shared/schema";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ClipboardList } from "lucide-react";
import PropertyMap from "@/components/property-map";
import { AddPropertyDialog } from "@/components/add-property-dialog";
import { usePageTitle } from "@/hooks/use-page-title";

export default function AppUnderwriting() {
  const queryClient = useQueryClient();
  usePageTitle("Underwriting");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { canEdit: canEditArea } = usePermissions();
  const readOnly = !canEditArea("underwriting");

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const createAnalysisMutation = useMutation({
    mutationFn: async (propertyId: number) => {
      await apiRequest("POST", `/api/app/underwriting/${propertyId}`, {});
    },
    onSuccess: (_data, propertyId) => {
      navigate(`/app/underwriting/${propertyId}`);
    },
    onError: (error: Error) => {
      if (error.message.includes("409")) {
        toast({ title: "Analysis already exists" });
      } else {
        toast({ title: "Failed to create analysis", variant: "destructive" });
      }
    },
  });

  const properties = propertiesQuery.data || [];
  const underwritingProperties = properties.filter((p) => p.status === "underwriting");

  const handleOpenAnalysis = async (propertyId: number) => {
    try {
      const res = await fetch(`/api/app/underwriting/${propertyId}`, { credentials: "include" });
      if (res.ok) {
        navigate(`/app/underwriting/${propertyId}`);
      } else if (res.status === 404) {
        createAnalysisMutation.mutate(propertyId);
      } else {
        const data = await res.json().catch(() => ({ message: "Server error" }));
        toast({ title: data.message || "Failed to open analysis", variant: "destructive" });
      }
    } catch {
      toast({ title: "Network error", variant: "destructive" });
    }
  };

  return (
    <AppLayout>
      <div className="p-6">
        <ViewOnlyBanner area="underwriting" />
        <div className="flex flex-wrap items-center justify-between gap-y-3 mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-underwriting-heading" data-tour="underwriting-heading">Underwriting</h1>
          <div className="flex flex-wrap items-center gap-2">
            <AddPropertyDialog
              forceStatus="underwriting"
              disabled={readOnly}
              triggerTestId="button-add-property-underwriting"
              data-tour="underwriting-new"
              onCreated={(prop) => {
                queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
                toast({ title: "Property added — opening underwriting analysis" });
                navigate(`/app/underwriting/${prop.id}`);
              }}
            />
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="underwriting" />
              <GuideButton pageKey="underwriting" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="underwriting" />
        </div>

        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-0">
            {([
              { key: "analyses", label: "Analyses", href: "/app/underwriting" },
              { key: "defaults", label: "Defaults", href: "/app/underwriting/defaults" },
            ]).map((tab) => {
              const isActive = tab.key === "analyses";
              return (
                <a
                  key={tab.key}
                  href={tab.href}
                  onClick={(e) => { e.preventDefault(); navigate(tab.href); }}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    isActive
                      ? "border-brand text-brand"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  data-testid={`tab-underwriting-${tab.key}`}
                >
                  {tab.label}
                </a>
              );
            })}
          </div>
        </div>

        {propertiesQuery.isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        ) : underwritingProperties.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2" data-testid="text-no-underwriting">No underwriting properties</p>
            <p className="text-sm text-gray-400">Add a property with "Underwriting" status to get started</p>
          </div>
        ) : (
          <>
            <PropertyMap
              properties={underwritingProperties}
              linkPattern="/app/underwriting/:id"
            />
            <div className="border rounded-lg overflow-hidden" data-tour="underwriting-list">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead data-testid="th-uw-property">Property</TableHead>
                    <TableHead data-testid="th-uw-address">Address</TableHead>
                    <TableHead data-testid="th-uw-rooms">Rooms</TableHead>
                    <TableHead data-testid="th-uw-actions">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {underwritingProperties.map((prop) => (
                    <TableRow key={prop.id} data-testid={`row-underwriting-${prop.id}`}>
                      <TableCell className="font-medium text-gray-900" data-testid={`text-uw-name-${prop.id}`}>
                        {prop.name}
                      </TableCell>
                      <TableCell className="text-gray-500" data-testid={`text-uw-address-${prop.id}`}>
                        {prop.address || "—"}
                      </TableCell>
                      <TableCell data-testid={`text-uw-rooms-${prop.id}`}>{prop.roomCount}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          className="bg-brand hover:bg-brand-hover"
                          onClick={() => handleOpenAnalysis(prop.id)}
                          disabled={createAnalysisMutation.isPending}
                          data-testid={`button-open-analysis-${prop.id}`}
                        >
                          Open Analysis
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
